'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { SegmentedControl } from '@/shared/ui';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { usePalettes } from '@/features/palette';
import { useSubscriptionLimits } from '@/features/subscription';
import { cn } from '@/shared/lib/cn';
import { ReferenceSourceGroup } from './ReferenceSourceGroup';
import { CustomColorList } from './CustomColorList';
import { PaletteList } from './PaletteList';

export interface ColorsContentProps {
  colors: string[] | null;
  onColorsChange: (colors: string[] | null) => void;
  projectId?: string;
  colorProjectReferenceIds?: string[] | null;
  onColorProjectReferencesChange?: (ids: string[] | null) => void;
  useAutoColorRefs?: boolean;
  onUseAutoColorRefsChange?: (value: boolean) => void;
  variant?: 'default' | 'compact';
  onNavigateToReferences?: () => void;
  /**
   * A picked product already carries a colour, so the render takes its palette
   * from Brands and this axis does not reach the image. Nothing is disabled —
   * the user may be mid-edit, and clearing their palette to make a point would
   * destroy a selection they can't get back.
   */
  productsSupplyColor?: boolean;
}

export function ColorsContent({
  colors,
  onColorsChange,
  projectId,
  colorProjectReferenceIds,
  onColorProjectReferencesChange,
  useAutoColorRefs = false,
  onUseAutoColorRefsChange,
  variant = 'default',
  onNavigateToReferences,
  productsSupplyColor = false,
}: ColorsContentProps) {
  const t = useTranslations('render.settings.colors');
  const { palettes, isLoading } = usePalettes();
  const limits = useSubscriptionLimits();
  const maxReferences = limits?.simultaneous_references || 3;
  const isCompact = variant === 'compact';

  const PRESET_OPTIONS = [
    { id: 'palette', label: t('palette') },
    { id: 'custom', label: t('custom') },
  ];

  // Cache for preserving user selections.
  // Custom mode starts empty — the editor surfaces a single "Add color"
  // affordance so users opt in deliberately. Cross-platform parity:
  // Apple/Android also default to an empty `customColors` list.
  const [colorCache] = useState(() => ({
    customColors: [] as string[],
    lastPaletteId: null as number | null,
  }));

  // Each tab owns the whole selection — a palette and free-form hexes are
  // mutually exclusive, so a tab never inherits the other's entries.
  //
  // Real manufacturer products used to live here as two more tabs. They moved
  // to the Brands setting: a siding product is a wall covering with a material
  // and a geometry, not a colour. Their tokens can still arrive inside `colors`
  // from a render made before the move — migrateLegacyProductPicks lifts those
  // onto the Brands axis when the form loads, so nothing here has to know about
  // them.
  const getCustomColors = useCallback((): string[] => {
    if (!colors || colors.length === 0) return colorCache.customColors;
    if (colors[0]?.startsWith('palette:')) return colorCache.customColors;
    return colors;
  }, [colors, colorCache]);

  const parseColorsMode = (colorsValue: string[] | null) => {
    if (!colorsValue || colorsValue.length === 0) {
      return { mode: 'palette', paletteId: null };
    }
    if (colorsValue.length === 1 && colorsValue[0]?.startsWith('palette:')) {
      const paletteId = parseInt(colorsValue[0].substring(8));
      return { mode: 'palette', paletteId };
    }
    return { mode: 'custom', paletteId: null };
  };

  const currentMode = parseColorsMode(colors);
  const [selectedPreset, setSelectedPreset] = useState<string>(
    currentMode.mode
  );
  const [selectedPaletteId, setSelectedPaletteId] = useState<number | null>(
    currentMode.paletteId
  );

  useEffect(() => {
    // Empty colors are ambiguous — map equally to Auto, Palette-with-no-id,
    // and Custom-with-no-colors. Preserve the user's current tab choice in
    // that case so clicking the Custom tab on a blank list (now the cross-
    // platform default) doesn't silently snap back to Palette.
    if (!colors || colors.length === 0) {
      setSelectedPaletteId(null);
      return;
    }

    const { mode, paletteId } = parseColorsMode(colors);
    setSelectedPreset(mode);
    setSelectedPaletteId(paletteId);

    if (mode === 'custom') {
      colorCache.customColors = colors;
    } else if (mode === 'palette' && paletteId) {
      colorCache.lastPaletteId = paletteId;
    }
  }, [colors, colorCache]);

  const handlePresetClick = (presetId: string) => {
    if (selectedPreset === 'custom' && colors && !colors[0]?.startsWith('palette:')) {
      colorCache.customColors = colors;
    } else if (selectedPreset === 'palette' && selectedPaletteId) {
      colorCache.lastPaletteId = selectedPaletteId;
    }

    setSelectedPreset(presetId);

    if (presetId === 'palette') {
      const paletteToSelect = colorCache.lastPaletteId;
      if (paletteToSelect) {
        setSelectedPaletteId(paletteToSelect);
        onColorsChange([`palette:${paletteToSelect}`]);
      } else {
        setSelectedPaletteId(null);
        onColorsChange(null);
      }
    } else if (presetId === 'custom') {
      onColorsChange(colorCache.customColors);
    }
  };

  const hasSelectedRefs = (colorProjectReferenceIds?.length ?? 0) > 0;

  const handlePaletteClick = (paletteId: number) => {
    if (selectedPreset !== 'palette') {
      setSelectedPreset('palette');
    }
    // Palette pick is mutually exclusive with project references: clear refs
    // and drop the reference source to "No references".
    if (hasSelectedRefs) {
      onColorProjectReferencesChange?.(null);
    }
    onUseAutoColorRefsChange?.(false);
    setSelectedPaletteId(paletteId);
    colorCache.lastPaletteId = paletteId;
    onColorsChange([`palette:${paletteId}`]);
  };

  const handleSelectNone = () => {
    onUseAutoColorRefsChange?.(false);
    onColorProjectReferencesChange?.(null);
  };

  const handleSelectAll = () => {
    // "All project references" is mutually exclusive with palette/custom
    // colors — clear the chosen colors and reset the editor to the palette tab.
    setSelectedPaletteId(null);
    colorCache.lastPaletteId = null;
    onColorsChange(null);
    setSelectedPreset('palette');
    onColorProjectReferencesChange?.(null);
    onUseAutoColorRefsChange?.(true);
  };

  const handleSelectiveChange = (ids: string[]) => {
    if (ids.length > 0) {
      setSelectedPaletteId(null);
      colorCache.lastPaletteId = null;
      onColorsChange(null);
      setSelectedPreset('palette');
    }
    onColorProjectReferencesChange?.(ids.length > 0 ? ids : null);
  };

  const handleCustomColorsChange = (newColors: string[]) => {
    // Custom colors are part of the "library" axis — mutually exclusive with
    // project references.
    if (newColors.length > 0) {
      if (hasSelectedRefs) {
        onColorProjectReferencesChange?.(null);
      }
      onUseAutoColorRefsChange?.(false);
    }
    colorCache.customColors = newColors;
    onColorsChange(newColors);
  };

  return (
    <div className={cn('space-y-4', !isCompact && 'space-y-6')}>
      {productsSupplyColor && (
        <NoticeBanner variant="info" message={t('fromProductsNotice')} />
      )}

      {/* Reference source: No references / All project references /
          Selective references (one always selected). */}
      {projectId && onColorProjectReferencesChange && (
        <ReferenceSourceGroup
          projectId={projectId}
          category="color"
          selectedIds={colorProjectReferenceIds || []}
          useAllRefs={useAutoColorRefs}
          maxSelection={maxReferences}
          onSelectNone={handleSelectNone}
          onSelectAll={handleSelectAll}
          onSelectiveChange={handleSelectiveChange}
          onNavigateToReferences={onNavigateToReferences}
        />
      )}

      {/* Mode toggle */}
      <div className={cn(!isCompact && 'flex justify-center')}>
        <SegmentedControl
          options={PRESET_OPTIONS}
          value={selectedPreset}
          onChange={handlePresetClick}
          size={isCompact ? 'sm' : 'md'}
          fullWidth={isCompact}
          className={cn(!isCompact && 'w-full md:w-auto')}
        />
      </div>

      {/* Palettes */}
      {selectedPreset === 'palette' && (
        <PaletteList
          palettes={palettes}
          isLoading={isLoading}
          selectedPaletteId={selectedPaletteId}
          onPaletteClick={handlePaletteClick}
          variant={variant}
        />
      )}

      {/* Custom Colors */}
      {selectedPreset === 'custom' && (
        <CustomColorList
          colors={getCustomColors()}
          onColorsChange={handleCustomColorsChange}
          maxColors={5}
          variant={variant}
        />
      )}
    </div>
  );
}

// Helper to get subtitle for preview. When the picker is in Auto mode and
// no palette was picked, the References row in the main settings list
// collapses the all/no refs toggle out of view — `autoLabel` should be
// "Auto (all refs)" / "Auto (no refs)" so the active variant stays visible.
// Mirrors Android `getAutoModeDisplayText()` and Apple `autoModeLabel()`.
export function getColorsSubtitle(
  colors: string[] | null,
  palettes: { id: string; attributes: { name: string; colors?: string[] } }[],
  t: (key: string) => string,
  autoLabel: string,
  colorProjectReferenceIds?: string[] | null
): string {
  if (colorProjectReferenceIds && colorProjectReferenceIds.length > 0) {
    return t('fromReference');
  }
  if (!colors || colors.length === 0) {
    return autoLabel;
  }
  if (colors[0]?.startsWith('palette:')) {
    const paletteId = parseInt(colors[0].substring(8));
    const palette = palettes.find((p) => parseInt(p.id) === paletteId);
    return palette?.attributes.name || autoLabel;
  }
  return t('custom');
}
